import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { HiOutlineChevronDown } from "react-icons/hi";

/** Read-only access disclosure, portalled so table overflow cannot clip it. */
export function McpTokenAccess({
  scopes,
  name,
}: {
  scopes: string[];
  name: string;
}) {
  const { t } = useTranslation("workspace");
  const [open, setOpen] = useState(false);
  const titleId = useId();
  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: "bottom-start",
    strategy: "fixed",
    middleware: [offset(8), flip(), shift({ padding: 12 })],
    whileElementsMounted: autoUpdate,
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context),
    useDismiss(context),
    useRole(context, { role: "dialog" }),
  ]);
  return (
    <>
      <button
        ref={refs.setReference}
        type="button"
        className="inline-flex items-center gap-2 whitespace-nowrap rounded-lg border border-border px-3 py-2 text-sm font-medium text-text hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        {...getReferenceProps()}
      >
        {t("mcp_tokens.access_count", { count: scopes.length })}
        <HiOutlineChevronDown
          aria-hidden
          className={`h-4 w-4 text-muted transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <FloatingPortal>
          <FloatingFocusManager
            context={context}
            modal={false}
            initialFocus={refs.floating}
          >
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps({
                tabIndex: -1,
                "aria-labelledby": titleId,
                className:
                  "z-50 w-80 max-w-[calc(100vw-24px)] overflow-hidden rounded-xl border border-border bg-surface-panel text-text shadow-lg outline-none",
              })}
            >
              <div className="border-b border-border px-4 py-3">
                <p id={titleId} className="text-sm font-semibold">
                  {t("mcp_tokens.access_column")}
                </p>
                <p className="mt-1 truncate text-xs text-muted" title={name}>
                  {name}
                </p>
              </div>
              <ul className="max-h-[min(20rem,50vh)] overflow-y-auto p-2">
                {scopes.map((scope) => (
                  <li key={scope} className="px-2 py-2 text-sm">
                    {t(`mcp_tokens.scope_labels.${scope.replace(":", "_")}`, {
                      defaultValue: scope,
                    })}
                  </li>
                ))}
              </ul>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
}
